import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions';
import type { TeamListItem, TeamsListFilters, TeamsListKPI } from '../types/teamsList.types';

export const teamsListService = {
  async getTeams(companyId: string, filters: TeamsListFilters, userId: string): Promise<{ data: TeamListItem[], total: number }> {
    await permissionEngine.assert(userId, 'teams.view', { companyId });
    
    let query = supabase
      .from('teams')
      .select(`
        *,
        project:projects!project_id(id, name),
        leader:user_profiles!leader_user_id(id, full_name, avatar_path),
        department:categories!department_id(id, name)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.search) {
      query = query.or(`name.ilike.%${filters.search}%,code.ilike.%${filters.search}%`);
    }

    if (filters.category && filters.category !== 'all') {
      query = query.eq('service_category', filters.category);
    }

    if (filters.status && filters.status !== 'all') {
      query = query.eq('status_code', filters.status);
    }

    if (filters.department && filters.department !== 'all') {
      query = query.eq('department_id', filters.department);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error, count } = await query;
    if (error) throw error;

    const teamIds = (data || []).map(t => t.id);
    if (teamIds.length === 0) return { data: [], total: 0 };

    const { data: memberCounts, error: mError } = await supabase
      .from('team_members')
      .select('team_id')
      .in('team_id', teamIds)
      .eq('is_active', true);

    if (mError) throw mError;

    const countMap = (memberCounts || []).reduce((acc: any, curr: any) => {
      acc[curr.team_id] = (acc[curr.team_id] || 0) + 1;
      return acc;
    }, {});

    const mappedData: TeamListItem[] = (data || []).map(item => ({
      ...item,
      leader: item.leader ? {
        id: item.leader.id,
        full_name: item.leader.full_name,
        avatar_url: item.leader.avatar_path
      } : undefined,
      department_name: item.department?.name,
      member_count: countMap[item.id] || 0
    }));

    return {
      data: mappedData,
      total: count || 0
    };
  },

  async getKPIs(companyId: string, userId: string): Promise<TeamsListKPI> {
    await permissionEngine.assert(userId, 'teams.view', { companyId });
    
    const { data, error } = await supabase
      .from('teams')
      .select('status_code')
      .eq('company_id', companyId);

    if (error) throw error;

    const stats = {
      total: data.length,
      active: data.filter(t => t.status_code === 'active').length,
      paused: data.filter(t => t.status_code === 'inactive').length,
      disbanded: data.filter(t => t.status_code === 'on_leave').length,
    };

    return stats;
  },

  async deleteTeam(id: string, companyId: string, userId: string): Promise<void> {
    await permissionEngine.assert(userId, 'teams.delete', { companyId, teamId: id });
    
    const { error } = await supabase
      .from('teams')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};
